import { DndContext } from '@dnd-kit/core';
import { Update } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Grid, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { LocationMapAccordianFragment } from './LocationMapAccordianFragment';
import { ReportedInformationFragment } from './ReportedInformationFragment';
import { DispatchShipmentFragment } from './ReportedInformationFragment/DispatchShipmentFragment';
import { ServiceActionBarFragment } from './ServiceActionBarFragment';
import { ServiceAdministrationFragment } from './ServiceAdministrationFragment';
import { ServiceHistoryFragment } from './ServiceHistoryFragment';
import { ServiceLogNoteFragment } from './ServiceLogNoteFragment/ServiceLogNoteFragment';
import { ServicePartAccordionFragment } from './ServicePartAccordionFragment';
import { ServiceSummaryFragment } from './ServiceSummaryFragment';
import {
  AddTagFabButton,
  ServiceUpdateTypography,
  ServiceSummeryBox,
  Container,
  ContentBox,
  Footer,
  LoadingWrapper,
  ServiceUpdateHeadingGrid
} from './UpdateService.styles';
import { ServiceDispatchShipmentData, ServiceObject } from '../../../@types/service.type';
import { Chip } from '../../../components/atoms/Chip';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { TypographyVariantProps } from '../../../components/atoms/Typography';
import { Button, ButtonVariantProps } from '../../../components/atoms/Button';
import { Loader, LoaderColorProps } from '../../../components/atoms/Loader';
import { SAMPLE_SERVICE, SERVICE_DISPATCH_SHIPMENT_DATA } from '../../../constants/service.constant';
import {
  AutocompleteTagType,
  getNewTagsMapped,
  getSelectedExistingTagsMapped,
  mapSavedTags,
  Tags
} from '../../../components/organisms/Tags';
import {
  ActionPermissions,
  CustomPermission,
  Tag,
  FontSizeProps,
  ScreenSize
} from '../../../@types';
import { EntityType, ServiceTypeCode } from '../../../configs/enums';
import { useGetServiceLogsByServiceId } from '../../../queries/service-log-query';
import { useGetServiceById } from '../../../queries/service-query';
import { useGetServiceTypesByCodeForServiceUpdate } from '../../../queries/service-type-query';
import { useGetAllTags, useAddTags } from '../../../queries/tags-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { isUserHasPermission } from '../../../configs/permissions';
import { IS_ADMIN_PANEL_ON_TOP } from '../../../configs/envs';
import { NotFoundError } from '../../Error/NotFound';
import { HistoryDataType } from '../../../components/molecules/TrackingHistory';
import { PLATFORM_NAME } from '../../../configs/common';
import { Modal } from '../../../components/atoms/Modal';
import { CloseIcon } from '../../../components/atoms/CloseIcon';

export const UpdateService: React.FC = () => {
  const { id } = useParams();
  const serviceId = id ? parseInt(id) : -1;
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ServiceObject>(SAMPLE_SERVICE);
  const [selectedTags, setSelectedTags] = useState<(AutocompleteTagType | string)[]>([]);
  const [newTags, setNewTags] = useState<Tag[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [permissions, setPermissions] = useState<CustomPermission[] | null>(null);
  const [adminPanelOnTop, setAdminPanelOnTop] = useState(false);
  const [serviceLogData, setServiceLogData] = useState<HistoryDataType[]>([]);
  const [dispatchData, setDispatchData] = useState<ServiceDispatchShipmentData>(SERVICE_DISPATCH_SHIPMENT_DATA);
  const [isMobile, setIsMobile] = useState(false);
  const [isSerialNumberAtAnyLocation, setIsSerialNumberAtAnyLocation] = useState<boolean>(false);

  const getServiceById = useGetServiceById(serviceId);
  const userTagsQuery = useGetAllTags(EntityType.TYPE_SERVICES);
  const updateTagsQuery = useAddTags();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const getServiceLogsByServiceIdQuery = useGetServiceLogsByServiceId(serviceId);
  const getServiceType = useGetServiceTypesByCodeForServiceUpdate(data.serviceTypeCode);

  useEffect(() => {
    setIsMobile(isMobileView);
  }, [isMobileView]);

  useEffect(() => {
    if (getServiceLogsByServiceIdQuery.data) {
      const historyLogItems: HistoryDataType[] = [];
      getServiceLogsByServiceIdQuery?.data?.filter((filterdata) => !filterdata.isHidden).map((item) => {
        const isEditableInteraction = item.data?.interactionForms ? item.data?.interactionForms.isEditable : false;
        const isDownloadableInteraction = item.data?.interactionForms ? item.data?.interactionForms.isDownloadable : false;
        const historyLog = {
          id: item.id,
          time: item.time,
          user: item.userName,
          status: item.serviceStatusName,
          notes: item.notes,
          logTypeName: item.logTypeName,
          logTypeCode: item.logTypeCode,
          isEditableInteraction,
          isDownloadableInteraction,
          xmlFileKey: item?.xmlFileKey
        };
        historyLogItems.push(historyLog);
      });
      if(isMobile){
        historyLogItems.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
      } else{
        historyLogItems.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
      }
      setServiceLogData(historyLogItems);
    }
  }, [getServiceLogsByServiceIdQuery.data]);

  useEffect(() => {
    const isAdminOnTop = localStorage.getItem(IS_ADMIN_PANEL_ON_TOP);
    isAdminOnTop && setAdminPanelOnTop(JSON.parse(isAdminOnTop));
  }, []);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    if (getServiceById.data) {
      setData(getServiceById.data);
      setSelectedTags(mapSavedTags(getServiceById.data.tags));

      document.title = `Service: ${getServiceById.data.id} | Services | ${PLATFORM_NAME}`;
    }
  }, [getServiceById.data]);

  useEffect(() => {
    if (userTagsQuery.data) {
      setAllTags(userTagsQuery.data);
    }
  }, [userTagsQuery.data]);

  useEffect(() => {
    if (getServiceType.data) {
      const target = getServiceType.data.targetOptions.find((target) => target.code === 'serial-any' && target.isDefault);
      target && setIsSerialNumberAtAnyLocation(true);
    }
  }, [getServiceType.data, data]);

  const handleTagBoxOpen = () => setOpen(true);
  const handleTagBoxClose = () => setOpen(false);

  const onChangeSelectedTags = (value: (string | AutocompleteTagType)[]) => {
    setSelectedTags(value);
  };

  const onChangeNewTags = (value: Tag[]) => {
    setNewTags(value);
  };

  const onChangeTags = async () => {
    const existingTags = getSelectedExistingTagsMapped(selectedTags);
    const freshTags = getNewTagsMapped(newTags);

    await updateTagsQuery.mutateAsync({
      entityTypeId: EntityType.TYPE_SERVICES,
      entityId: data.id,
      freshTags,
      existingTags
    });
    getServiceById.refetch();
    setOpen(false);
  };

  const renderComponent = () => {
    return <Grid marginTop={2}>
      {isUserHasPermission(ActionPermissions.Service_Edit_Administration_Section, permissions) && (
        <ServiceAdministrationFragment
          data={data}
          adminPanelOnTop={adminPanelOnTop}
          onClick={(event) => {
            event.stopPropagation();
            setAdminPanelOnTop(!adminPanelOnTop);
            localStorage.setItem(IS_ADMIN_PANEL_ON_TOP, JSON.stringify(!adminPanelOnTop));
          }}
        />
      )}
    </Grid>;
  };

  if (!getServiceById.data && id !== 'create') {
    return getServiceById.isLoading ? <LoadingWrapper><Loader color={LoaderColorProps.Success} /></LoadingWrapper> : <NotFoundError></NotFoundError>;
  }

  if (!getServiceLogsByServiceIdQuery.data && id !== 'create') {
    return getServiceLogsByServiceIdQuery.isLoading ? <LoadingWrapper><Loader color={LoaderColorProps.Success} /></LoadingWrapper> : <NotFoundError></NotFoundError>;
  }

  return (
    <PageContainer>
      <ServiceUpdateTypography variant={TypographyVariantProps.H6} fontWeight={600} isMobileView={isMobileView}>
        <ServiceUpdateHeadingGrid container spacing={1} isMobileView={isMobileView} hasTags={data.tags.length > 0}>
          <Grid>
            Service #{data.id} - {data.mainLocation}
          </Grid>
          {data.tags.map((tag: Tag) =>
            <Grid key={tag.id}>
              <Chip label={tag.text} backgroundColor={tag.colour} textColor="#ffffff" onClick={handleTagBoxOpen} />&nbsp;
            </Grid>
          )}
          {data.tags && data.tags.length == 0 && !isMobile &&
            <Grid marginTop={'4px'}>
              <AddTagFabButton onClick={handleTagBoxOpen} sx={{ zIndex: 1 }}>
                <AddIcon fontSize={FontSizeProps.Large} />
              </AddTagFabButton>
            </Grid>
          }
          {isUserHasPermission(ActionPermissions.Service_Edit_Service_Parameters_General, permissions) && (
            <Modal
              open={open}
              onClose={handleTagBoxClose}
              aria-labelledby="tag-modal-title"
              aria-describedby="tag-modal-description"
            >
              <Container>
                <CloseIcon handleClose={handleTagBoxClose} />
                <ContentBox>
                  <Tags
                    selectedTags={selectedTags}
                    newTags={newTags}
                    setSeletedTags={onChangeSelectedTags}
                    setNewTags={onChangeNewTags}
                    allTags={allTags}
                  />
                </ContentBox>
                <Footer direction="row-reverse" spacing={1}>
                  <Button variant={ButtonVariantProps.Primary} startIcon={<Update />} onClick={onChangeTags}>
                    Save
                  </Button>
                </Footer>
              </Container>
            </Modal>
          )}
        </ServiceUpdateHeadingGrid>
      </ServiceUpdateTypography>
      <ServiceSummeryBox>
        <ServiceActionBarFragment data={data} />
        <ServiceSummaryFragment data={data} />
      </ServiceSummeryBox>
      {data.reportedSerialNumber && isUserHasPermission(ActionPermissions.Service_Edit_Part_Section, permissions) && isSerialNumberAtAnyLocation && 
      <Grid marginTop={2}>
        <ServicePartAccordionFragment service={data} partId={data.reportedSerialNumberPartId} expand={data.serviceTypeCode === ServiceTypeCode.Repair} />
      </Grid>
      }
      {adminPanelOnTop && renderComponent()}
      <Grid marginTop={2}>
        <LocationMapAccordianFragment data={data} isService={true} />
      </Grid>
      <Grid container direction="row" marginTop={2}>
        <Grid width={dispatchData.exited ? '100%' : '50%'}>
          <ReportedInformationFragment data={data} onDispatch={(dispatchData) => setDispatchData(dispatchData)} />
        </Grid>
        <Grid width="50%">
          <DndContext>
            <DispatchShipmentFragment data={data} dispatchData={dispatchData} onDispatch={(dispatchData) => setDispatchData(dispatchData)} />
          </DndContext>
        </Grid>
      </Grid>
      {data.reportedSerialNumber && isUserHasPermission(ActionPermissions.Service_Edit_Part_Section, permissions) && !isSerialNumberAtAnyLocation && 
      <Grid marginTop={2}>
        <ServicePartAccordionFragment service={data} partId={data.reportedSerialNumberPartId} expand={data.serviceTypeCode === ServiceTypeCode.Repair} />
      </Grid>
      }
      {isUserHasPermission(ActionPermissions.Service_Edit_Add_Notes, permissions) &&
        <Grid marginTop={2}>
          <ServiceLogNoteFragment data={data} />
        </Grid>
      }
      <Grid marginTop={2}>
        <ServiceHistoryFragment serviceLogData={serviceLogData} permissions={permissions} serviceId={data.id} service={data} />
      </Grid>
      {!adminPanelOnTop && renderComponent()}
    </PageContainer>
  );
};